import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { getPacksApi, deleteData } from "../../services/apiServices";
import { Spinner, Button, Container, Badge, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AllPacks = () => {
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchPacks = async (page = 1, limit = 10) => {
    try {
      setPending(true);
      const response = await getPacksApi(page, limit);

      if (response.status && response.data) {
        setRows(response.data.packs || []);
        setTotalRows(response.data.pagination?.total || 0);
      } else {
        setError("Invalid API response structure");
      }
    } catch (err) {
      setError("Failed to fetch packs");
      toast.error("Failed to fetch packs");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    fetchPacks(page, perPage);
  }, [page, perPage]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pack?")) return;

    try {
      await deleteData(`api/v2/admin/auth/packs/${id}`);
      toast.success("Pack deleted successfully");
      fetchPacks(page, perPage);
    } catch (error) {
      toast.error("Failed to delete pack");
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const columns = [
    {
      name: "Pack Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <strong>{row.name}</strong>
          <div className="text-muted small">
            ID: {row._id.substring(0, 8)}...
          </div>
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Creator",
      selector: (row) => row.creator,
      sortable: true,
      cell: (row) => (
        <div>
          <div>{row.creator}</div>
          <div className="text-muted small">
            ID: {row?.creatorId?.substring(0, 8)}...
          </div>
        </div>
      ),
      minWidth: "150px",
    },
    {
      name: "Pack Amount",
      selector: (row) => row.packAmount,
      sortable: true,
      cell: (row) => (
        <Badge color="primary" pill className="fs-6">
          {row.packAmount}
        </Badge>
      ),
      minWidth: "120px",
    },
    {
      name: "Items",
      selector: (row) => row.items?.length || 0,
      sortable: true,
      cell: (row) => (
        <Badge color="info" pill className="fs-6">
          {row.items?.length || 0}
        </Badge>
      ),
      minWidth: "100px",
    },
    {
      name: "Total Value",
      selector: (row) =>
        row.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0,
      sortable: true,
      cell: (row) => {
        const totalValue =
          row.items?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
        return (
          <div className="fw-bold text-success">
            {formatCurrency(totalValue)}
          </div>
        );
      },
      minWidth: "150px",
    },
    {
      name: "Outcome",
      selector: (row) => row.outCome,
      sortable: true,
      cell: (row) => (
        <Badge
          color={row.outCome >= 1 ? "success" : "warning"}
          pill
          className="fs-6">
          {row.outCome}x
        </Badge>
      ),
      minWidth: "100px",
    },
    {
      name: "Status",
      selector: (row) => row.isApproved,
      sortable: true,
      cell: (row) => (
        <Badge
          color={row.isApproved ? "success" : "danger"}
          pill
          className="fs-6">
          {row.isApproved ? "Approved" : "Pending"}
        </Badge>
      ),
      minWidth: "120px",
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => {
        const date = new Date(row.createdAt);
        return (
          <div className="small">
            {date.toLocaleDateString()}
            <br />
            {date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        );
      },
      minWidth: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <Button
            size="sm"
            color="info"
            onClick={() => navigate(`/packs/view/${row._id}`)}
            title="View Pack Details"
            className="mb-1">
            <i className="ri-eye-line" />
          </Button>

          <Button
            size="sm"
            color="warning"
            onClick={() => navigate(`/packs/edit/${row._id}`)}
            title="Edit Pack"
            className="mb-1">
            <i className="ri-edit-line" />
          </Button>

          <Button
            size="sm"
            color="danger"
            onClick={() => handleDelete(row._id)}
            title="Delete Pack"
            className="mb-1">
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
      minWidth: "180px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  const customStyles = {
    table: {
      style: {
        width: "100%",
      },
    },
    tableWrapper: {
      style: {
        display: "table",
        width: "100%",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        fontSize: "14px",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        paddingLeft: "16px",
        paddingRight: "16px",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
  };

  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PackDraw" breadcrumbItem="All Packs" />

          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="mb-0">Packs Management</h4>
            </Col>
            <Col className="text-end">
              <Button
                color="primary"
                onClick={() => navigate("/add-pack")}
                className="d-inline-flex align-items-center gap-2">
                <i className="ri-add-line"></i>
                Add New Pack
              </Button>
            </Col>
          </Row>

          {/* Full width DataTable */}
          <div className="w-100 overflow-auto">
            <DataTable
              columns={columns}
              data={rows}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={pending}
              progressComponent={
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              }
              highlightOnHover
              responsive={false} // Disable built-in responsive to control width
              customStyles={customStyles}
              noDataComponent={
                <div className="text-center py-5">
                  <i className="ri-inbox-line display-4 text-muted"></i>
                  <h5 className="mt-3">No packs found</h5>
                  <p className="text-muted">
                    Start by creating your first pack
                  </p>
                </div>
              }
              className="w-100"
            />
          </div>

          {/* Stats Summary - Full width cards */}
          <Row className="mt-4 g-3">
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Total Packs</p>
                      <h4 className="mb-0">{totalRows}</h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                        <i className="ri-box-3-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Approved Packs</p>
                      <h4 className="mb-0">
                        {rows.filter((pack) => pack.isApproved).length}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-4">
                        <i className="ri-check-double-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Total Items</p>
                      <h4 className="mb-0">
                        {rows.reduce(
                          (sum, pack) => sum + (pack.items?.length || 0),
                          0,
                        )}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-4">
                        <i className="ri-list-check"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Pending Approval</p>
                      <h4 className="mb-0">
                        {rows.filter((pack) => !pack.isApproved).length}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-4">
                        <i className="ri-time-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AllPacks;
